import { ColumnDef } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { Pencil, Trash2 } from 'lucide-react';

import { ScrollableCell } from '@/components/data-table/cells';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TruncatedTooltip } from '@/components/ui/truncated-tooltip';

import { PaymentDetailsEntry } from '../types/payment-details';

const I18N = 'pages.finance.paymentDetails';

interface GetPaymentDetailsColumnsParams {
  t: TFunction<'authenticated.settings'>;
  onEdit: (entry: PaymentDetailsEntry) => void;
  onDelete: (entry: PaymentDetailsEntry) => void;
}

export const getPaymentDetailsColumns = ({
  t,
  onEdit,
  onDelete,
}: GetPaymentDetailsColumnsParams): ColumnDef<PaymentDetailsEntry>[] => [
  {
    accessorKey: 'name',
    size: 160,
    minSize: 120,
    header: () => <div className="min-w-[160px]">{t(`${I18N}.table.headers.name`)}</div>,
    cell: ({ row }) => (
      <ScrollableCell text={row.original.name} className="min-w-[160px] font-medium" />
    ),
  },
  {
    accessorKey: 'details',
    size: 280,
    minSize: 200,
    header: () => <div className="min-w-[280px]">{t(`${I18N}.table.headers.details`)}</div>,
    cell: ({ row }) => (
      <TruncatedTooltip
        content={row.original.details}
        maxLines={2}
        preserveWhitespace
        className="min-w-[280px] text-sm text-muted-foreground"
      />
    ),
  },
  {
    id: 'default',
    size: 90,
    minSize: 90,
    header: () => <div className="min-w-[90px]">{t(`${I18N}.table.headers.default`)}</div>,
    cell: ({ row }) =>
      row.original.isDefault ? (
        <Badge variant="secondary">{t(`${I18N}.table.defaultBadge`)}</Badge>
      ) : null,
  },
  {
    id: 'actions',
    size: 75,
    minSize: 75,
    header: () => (
      <div className="flex w-full min-w-[75px] justify-end">
        {t(`${I18N}.table.headers.actions`)}
      </div>
    ),
    cell: ({ row }) => (
      <div className="flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => onEdit(row.original)}
        >
          <Pencil className="h-4 w-4 text-muted-foreground" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => onDelete(row.original)}
        >
          <Trash2 className="h-4 w-4 text-muted-foreground" />
        </Button>
      </div>
    ),
  },
];
